/**
 * Device approval (spec `docs/plans/2026-09-02-next-steps-e2e-encryption.md`,
 * sections 6.2, 6.6 and 6.7; API contract sections 4-6).
 *
 * The whole of what makes the two Part 1 screens live. `ApproveThisDevice.vue`
 * and `NewDeviceRequest.vue` perform nothing at all — every wrapping, every
 * signature and every dispatch is here — which is what lets those screens be
 * tested as pure renderings and this file be tested without a component.
 *
 * WHAT IT DECIDES. On a device that cannot read the member's notes:
 *
 *   platform copy restored  →  mint a device pair, wrap MK to it, register
 *                              SIGNED  →  APPROVED without asking anyone
 *   no key at all           →  mint a device pair, register UNSIGNED
 *                              →  PENDING, and wait
 *   already waiting         →  wait
 *   wrapping is there       →  unwrap it, store it, re-read the annotations
 *   refused                 →  say so, and offer a new identity
 *
 * And on a device that holds MK: wrap MK to a device asking to join, or refuse
 * it.
 *
 * NO VUE, NO SCREEN, NO KEY OF ITS OWN. Key material lives in `courses-keys`
 * and is read through its getters; the device list and every request live in
 * `courses-devices`. This module is the only place that knows both, which is
 * the reason it exists: the crypto needs the key, the API needs the status, and
 * neither module may import the other.
 *
 * THE ONE THING THAT CANNOT BE GOT WRONG QUIETLY is the set of fields a
 * signature covers, and this module deliberately does not decide it:
 * `courses-encryption/signer.js` owns that table for the whole app, and its
 * comment carries the wire-level reason for each. All that happens here is
 * reading the store for the key and the bundle it is wrapped under.
 */
import {
  DEVICE_STATUSES,
  ENCRYPTION_ERROR_CODES,
  API_ERROR_CODES,
  isEncryptionApiError,
} from '@/utils/courses-encryption-api';
import { UNREGISTERED } from '@/store/modules/coursesDevices';
import { describeThisDevice, newDeviceId } from './this-device';

/** What one call here can end as. */
export const APPROVAL_OUTCOME = Object.freeze({
  /** A restored platform copy proved itself: APPROVED, with no second device. */
  SELF_APPROVED: 'self-approved',
  /** Registered (or already registered) and waiting for an approved device. */
  PENDING: 'pending',
  /** MK is on this device now. The notes open. */
  APPROVED: 'approved',
  /**
   * APPROVED with `wrappedMk: null`. The API allows it — a registration that
   * proves itself is approved whether or not it sent a `selfWrapped` (contract
   * section 4) — and there is nothing this device can do about it: another
   * approved device has to wrap it one. Shown as waiting, under the same copy.
   */
  APPROVED_NO_WRAPPING: 'approved-no-wrapping',
  /** REVOKED: denied on request, or signed out afterwards. Needs a new identity. */
  DENIED: 'denied',
  /** 409 the pending cap. Five devices are already waiting. */
  TOO_MANY_PENDING: 'too-many-pending',
  /** The member is not enrolled at all: nothing to be approved for. */
  NOT_ENROLLED: 'not-enrolled',
  /** This device already holds the key. Nothing to do. */
  HAS_KEY: 'has-key',
  /** `reason` says what. Nothing was written. */
  ERROR: 'error',
});

/** How long to wait for a hydration still in flight behind a deep link. */
export const HYDRATION_TIMEOUT_MS = 3000;

/** How often the waiting device asks whether an approved one has answered. */
export const POLL_INTERVAL_MS = 4000;

/**
 * The crypto and the platform adapter, loaded at the point of use.
 *
 * WHY LAZY, and it is not about bundle size — `enrolment.js` gives the same
 * reason at length. The platform key adapter statically imports two NATIVE
 * plugins, and this module is imported by the Notes Hub, the reader and the
 * chooser. A static import would put the Keychain behind every unit spec that
 * mounts one of those pages. Memoised, so a second approval in one session does
 * not re-resolve the modules.
 *
 * @type {Promise<object> | null}
 */
let keyToolsPromise = null;

async function loadKeyTools() {
  if (!keyToolsPromise) {
    keyToolsPromise = Promise.all([
      import('@/utils/courses-crypto/device-key'),
      import('@/utils/courses-crypto/approval-secret'),
      import('@/utils/courses-crypto/bundle-key'),
      import('@/utils/courses-crypto/platform-copy'),
      import('@/utils/courses-crypto/platform-key-adapter'),
      import('@/utils/courses-crypto/recovery-code'),
      import('@/store/modules/coursesKeys'),
      import('@/utils/courses-encryption/signer'),
    ])
      .then(
        ([
          deviceKey,
          approvalSecret,
          bundleKey,
          platformCopy,
          adapter,
          recoveryCode,
          keysModule,
          signer,
        ]) => ({
          generateDeviceKeyPair: deviceKey.generateDeviceKeyPair,
          wrapMasterKeyToDevice: deviceKey.wrapMasterKeyToDevice,
          unwrapMasterKeyFromDevice: deviceKey.unwrapMasterKeyFromDevice,
          // #1693: the out-of-band S, minted beside the pair by the PENDING
          // device and shown on its own screen. It never reaches the API.
          generateApprovalSecret: approvalSecret.generateApprovalSecret,
          isTypeableApprovalSecret: approvalSecret.isTypeableApprovalSecret,
          wrappedMkHash: deviceKey.wrappedMkHash,
          recoveryWrappingHash: recoveryCode.recoveryWrappingHash,
          unwrapSigningKey: bundleKey.unwrapSigningKey,
          signOperation: bundleKey.signOperation,
          serialisePlatformCopy: platformCopy.serialisePlatformCopy,
          resolveAdapter: adapter.resolvePlatformAdapter,
          ENROLMENT_STATUS: keysModule.ENROLMENT_STATUS,
          createOperationSigner: signer.createOperationSigner,
        }),
      )
      .catch(error => {
        keyToolsPromise = null;
        throw error;
      });
  }
  return keyToolsPromise;
}

/**
 * Re-read what this device could not read before. The acceptance criterion is
 * that the notes open WITHOUT a reload, and this is how: every row that came
 * back sealed was recorded against its type, and a fresh fetch of that type
 * REPLACES the type's record wholesale (`courses.js` `recordUnreadable`, with
 * `append` false), so one re-fetch both fills the list and clears the lock.
 *
 * Deliberately not a mutation on `courses`: clearing the record without
 * re-fetching would leave the lists holding the sealed rows they already have,
 * and the member looking at an unlocked screen with nothing on it.
 *
 * The reader's blank answers are not here — they are per-lesson and the reader
 * re-fetches its own on entry.
 *
 * @param {object} store
 */
async function refreshAnnotations(store) {
  await Promise.all([
    store.dispatch('courses/fetchTags').catch(() => {}),
    store.dispatch('courses/fetchAllHighlights').catch(() => {}),
    store.dispatch('courses/fetchAllNotes').catch(() => {}),
  ]);
}

const DEFAULT_DEPS = Object.freeze({
  loadKeyTools,
  describeThisDevice,
  newDeviceId,
  refreshAnnotations,
});

const withDeps = overrides => (overrides ? { ...DEFAULT_DEPS, ...overrides } : DEFAULT_DEPS);

/**
 * Whether a status snapshot has been read at all, and the snapshot itself.
 *
 * The individual fields are read through `courses-devices`' own getters
 * (`keyId`, `signingKeyWrapped`, added by #1581). This stays because "has the
 * status been read yet" is a different question from any one field's value: a
 * getter answering `null` cannot tell a member who has never been looked up
 * from one whose bundle genuinely has no wrapping.
 *
 * @param {object} store
 */
const statusOf = store => store.state['courses-devices']?.status ?? null;

const outcome = (value, extra = {}) => ({ outcome: value, ...extra });

const failed = (reason, error) =>
  outcome(APPROVAL_OUTCOME.ERROR, { reason, ...(error ? { error: describeError(error) } : {}) });

/**
 * Why `adoptWrappedKey` could not take a wrapping that WAS there.
 *
 * Exported because the waiting screen has to tell one of these apart from the
 * rest. UNWRAP is the approver having typed the code wrongly — or, the case
 * decision 15 exists for, a substituted key — and it is deterministic: the
 * server hands back the same wrapping for as long as it is asked, so the
 * screen has to SAY so rather than keep asking. The others are a read or a
 * write that has not landed yet, and asking again is the right thing.
 */
export const ADOPT_FAILURE = Object.freeze({
  NO_WRAPPING: 'no-wrapping',
  NO_DEVICE_KEY: 'no-device-key',
  NO_KEY_ID: 'no-key-id',
  UNWRAP: 'unwrap-failed',
  PERSIST: 'persist-failed',
});

/**
 * A plain, loggable failure. Deliberately not the error itself: an
 * `EncryptionApiError` carries the axios error as its `cause`, and the request
 * config — with the bearer token in it — inside that.
 *
 * @param {unknown} error
 */
function describeError(error) {
  if (isEncryptionApiError(error)) {
    const { code, apiCode, status, message, retryable } = error;
    return { code, apiCode, status, message, retryable };
  }
  return {
    code: 'unknown',
    apiCode: null,
    status: null,
    message: String(error?.message ?? error),
    retryable: false,
  };
}

/**
 * A signer for `courses-devices`: `(nonce, { op, params }) => signature`.
 *
 * The field mapping this used to own now lives in
 * `courses-encryption/signer.js` (#1581), which is the only place in the app
 * that turns a store action's `params` into the canonical fields the API
 * verifies. What is left here is the part that needs the STORE: which member,
 * which master key, and which bundle wrapping to unwrap.
 *
 * Built fresh per operation rather than cached. MK can be wiped under us by a
 * sign-out, and a signer holding the previous member's key is the one thing
 * this must never do.
 *
 * @param {{ store: object, deps?: object }} args
 * @returns {Promise<(nonce: string, operation: { op: string, params?: object }) => Promise<string>>}
 * @throws {Error} when this device holds no key, or the bundle has not been read
 */
export async function signerFromStore({ store, deps: overrides } = {}) {
  const deps = withDeps(overrides);
  const tools = await deps.loadKeyTools();
  const keys = store.getters['courses-keys/keyMaterial'];
  if (!keys) {
    throw new Error('device-approval: this device holds no master key, so it cannot sign');
  }
  // #1581's getters, rather than the status snapshot this file used to read
  // through a private helper: same two fields, named once for every caller.
  const signingKeyWrapped = store.getters['courses-devices/signingKeyWrapped'];
  const keyId = store.getters['courses-devices/keyId'];
  if (!signingKeyWrapped || !keyId) {
    throw new Error('device-approval: the encryption status has not been read yet');
  }

  return tools.createOperationSigner({
    signingKeyWrapped,
    mk: keys.mk,
    keyId,
    memberId: store.getters['courses-keys/memberId'],
  });
}

/**
 * This install's registrable identity, minting one when there is none — or when
 * the caller wants a new one.
 *
 * The id has to survive a relaunch, so it is written through
 * `courses-keys/persist` alongside whatever key material that module already
 * holds (none, for a device that is waiting). Without that, a keyless device
 * would file a fresh request on every app open and leave a REVOKED row behind
 * each time.
 *
 * `persist` is the right action rather than a new one: it asserts the member,
 * refuses a store that cannot keep a key between launches, undoes its own write
 * if the member signed out meanwhile, and hands the identity to
 * `courses-devices` itself.
 *
 * ⚠️ `remember: false` is for the self-approve path, and it is not an
 * optimisation. `persist` commits `setKeys({ ...record, needsRegistration:
 * false })` — the flag is cleared by the WRITE, not by the registration — and
 * that flag is the only thing that brings a device holding a restored platform
 * copy back to `selfApprove`. Writing before the register is proven would
 * latch a failed self-approval permanently: MK on the device, no row on the
 * server, no way back to this path, and `needsApproval` still true. So that
 * path mints, hands the identity to `courses-devices` for the request, and
 * persists only once the API has answered.
 *
 * @param {{ store: object, deps: object, tools: object, fresh?: boolean, remember?: boolean }} args
 * @returns {Promise<{ deviceId: string, devicePublicKey: string, devicePrivateKey: CryptoKey }>}
 */
async function ensureLocalIdentity({ store, deps, tools, fresh = false, remember = true }) {
  const memberId = store.getters['courses-keys/memberId'];
  const held = store.getters['courses-keys/deviceIdentity'];
  const reusable =
    !fresh && held?.deviceId && held?.devicePublicKey && held?.devicePrivateKey ? held : null;

  // #1693. S IS BACK-FILLED, NOT A REASON TO MINT A NEW IDENTITY. A record
  // written by a build before Phase 2b has an id and a key pair and no secret,
  // and adding S to the reusability test above would make that device mint a
  // fresh identity on the update: a second registration, one of the five
  // pending slots spent, and a stale PENDING row the member is now looking at
  // on their other phone. So the identity is kept and only the missing field
  // is added.
  //
  // Sound because S is NOT bound to the key pair: it goes into the wrapping
  // key at approval time (`device-key.js`'s `deriveWrappingKey`), not into the
  // pair's generation, so an existing pair with a new S is exactly as good as
  // a pair minted beside it.
  //
  // AND AN S THE ALPHABET CANNOT SHOW IS REPLACED THE SAME WAY. Since the check
  // character (`approval-secret.js`), a typed code exists only for a secret
  // whose check value is under 32; new ones are drawn until it is, but five in
  // thirty-seven minted before that rule have none. Sound for the same reason:
  // S is not bound to the pair, and this device has never shown this S to
  // anyone — it is about to, which is exactly when it has to be showable.
  const heldSecret = reusable?.approvalSecret ?? null;
  const keepSecret = !!heldSecret && tools.isTypeableApprovalSecret(heldSecret);
  const backfilled = !!reusable && !keepSecret;
  const identity = reusable
    ? { ...reusable, approvalSecret: keepSecret ? heldSecret : tools.generateApprovalSecret() }
    : {
        deviceId: deps.newDeviceId(),
        approvalSecret: tools.generateApprovalSecret(),
        ...(await tools.generateDeviceKeyPair().then(pair => ({
          devicePublicKey: pair.publicKey,
          devicePrivateKey: pair.privateKey,
        }))),
      };

  // A back-fill has to be written too, or the secret this device is about to
  // SHOW on screen 2 is gone by the next launch and the approver's code opens
  // nothing.
  if ((!reusable || backfilled) && remember) {
    const keys = store.getters['courses-keys/keyMaterial'];
    await store.dispatch('courses-keys/persist', {
      memberId,
      keyId: keys?.keyId ?? null,
      mk: keys?.mk ?? null,
      ...identity,
    });
  }

  // FOUR FIELDS, BUILT EXPLICITLY — never `...identity`. `courses-devices`
  // puts what it is given on the wire, and S is the one value in this object
  // that must never reach the server: the whole of decision 15 is that it
  // travels by camera or by eye and by no other route.
  const { name, platform } = await deps.describeThisDevice();
  await store.dispatch('courses-devices/setLocalDevice', {
    id: identity.deviceId,
    publicKey: identity.devicePublicKey,
    name,
    platform,
  });
  return identity;
}

/**
 * Section 6.2's second branch: MK arrived on this phone from the platform copy,
 * so this install can prove it holds the key and be APPROVED without anybody
 * tapping anything.
 *
 * It has no device identity — the copy holds only `{ keyId, mk }` — so the pair
 * is minted here, MK is wrapped to its own public half, and the registration is
 * signed with the bundle key.
 *
 * @param {{ store: object, deps: object }} args
 */
async function selfApprove({ store, deps }, attempt = 0) {
  const tools = await deps.loadKeyTools();
  const memberId = store.getters['courses-keys/memberId'];
  const keys = store.getters['courses-keys/keyMaterial'];
  const status = statusOf(store);

  // The wrapping's AAD names a key generation, and so does the unwrap that
  // opens it on the next launch. A platform copy a generation behind the bundle
  // would produce a wrapping nobody could ever open, so it is refused here
  // rather than written and mourned later.
  if (keys.keyId !== status.keyId) {
    console.warn(
      '[CoursesE2EE] The platform copy names a different key generation than the bundle',
    );
    return failed('key-generation-mismatch');
  }

  // NOT remembered yet: the persist below, after the API has answered, is what
  // writes this identity — and what clears `needsRegistration`. See
  // `ensureLocalIdentity`.
  //
  // Guarded because `resolveKeylessDevice` is documented as never rejecting:
  // the caller is a screen opening, and it awaits this with nothing to catch a
  // throw, so a failure here would leave the page with no spinner, no error and
  // no outcome.
  let identity;
  try {
    identity = await ensureLocalIdentity({
      store,
      deps,
      tools,
      fresh: attempt > 0,
      remember: false,
    });
  } catch (error) {
    return failed('identity-failed', error);
  }

  let selfWrapped;
  let signer;
  try {
    // NO SECRET, so this stays `v: 1`, and that is correct rather than a gap
    // (#1693). Decision 15 defends against a public key SUBSTITUTED IN
    // TRANSIT; this wrapping goes to `identity.devicePublicKey`, a value this
    // device generated moments ago and which has not round-tripped through the
    // API — there is no key here for anybody to substitute, and no second
    // phone to read a code off. An S folded in would only be one this device
    // would have to hand itself back.
    selfWrapped = await tools.wrapMasterKeyToDevice({
      mk: keys.mk,
      keyId: status.keyId,
      memberId,
      devicePublicKey: identity.devicePublicKey,
    });
    // The wrapping decides the register field set, and it reaches the signer
    // through the store: `registerThisDevice` puts `selfWrapped` into the
    // signed `params` as well as into the body, so `FIELDS_FOR.register`
    // hashes the very object being sent rather than a copy.
    signer = await signerFromStore({ store, deps });
  } catch (error) {
    return failed('wrap-failed', error);
  }

  try {
    await store.dispatch('courses-devices/registerThisDevice', { selfWrapped, signer });
  } catch (error) {
    // The id belongs to ANOTHER member (device.service.ts:51) — a collision, or
    // a record restored from a phone signed in as somebody else. A second
    // identity is the only thing that can succeed, and one retry is the whole
    // of the answer.
    if (
      attempt === 0 &&
      isEncryptionApiError(error, ENCRYPTION_ERROR_CODES.CONFLICT) &&
      error.apiCode === API_ERROR_CODES.DEVICE_ID_IN_USE
    ) {
      return selfApprove({ store, deps }, attempt + 1);
    }
    // A proven registration skips the pending cap entirely
    // (device.service.ts:54 counts only when `!proven`), so this is defensive
    // — but the screen needs a sentence either way.
    if (
      isEncryptionApiError(error, ENCRYPTION_ERROR_CODES.CONFLICT) &&
      error.apiCode === API_ERROR_CODES.TOO_MANY_PENDING_DEVICES
    ) {
      return outcome(APPROVAL_OUTCOME.TOO_MANY_PENDING);
    }
    return failed('register-failed', error);
  }

  // `registerThisDevice` refreshes the status, and that read — not the row on
  // the wire — is the truth: `register` answers the member's own id with the row
  // as it ALREADY is, and never upgrades a REVOKED one. So a device whose
  // stored id was denied earlier has just been handed its own refusal back, and
  // needs a new identity to get anywhere.
  const registeredStatus = store.getters['courses-devices/thisDeviceStatus'];
  if (registeredStatus === DEVICE_STATUSES.REVOKED && attempt === 0) {
    return selfApprove({ store, deps }, attempt + 1);
  }
  if (registeredStatus === DEVICE_STATUSES.REVOKED) return outcome(APPROVAL_OUTCOME.DENIED);

  try {
    await store.dispatch('courses-keys/persist', {
      memberId,
      keyId: status.keyId,
      mk: keys.mk,
      ...identity,
    });
  } catch (error) {
    // The row is APPROVED and this device cannot keep its key. The next launch
    // reads the platform copy again and comes back here, which is a state that
    // resolves itself rather than one the member has to act on.
    return failed('persist-failed', error);
  }

  store.commit('courses-keys/setEnrolment', {
    status: tools.ENROLMENT_STATUS.ENROLLED,
    hasRecoveryCode: store.getters['courses-devices/hasRecoveryCode'] === true,
  });
  await deps.refreshAnnotations(store);
  return outcome(APPROVAL_OUTCOME.SELF_APPROVED, { deviceId: identity.deviceId });
}

/**
 * MK, wrapped to this device by an approved one, is waiting on the status
 * snapshot. Open it, store it, and re-read what could not be read before.
 *
 * @param {{ store: object, deps?: object }} args
 */
export async function adoptWrappedKey({ store, deps: overrides } = {}) {
  const deps = withDeps(overrides);
  const tools = await deps.loadKeyTools();
  const memberId = store.getters['courses-keys/memberId'];
  const wrapping = store.getters['courses-devices/wrappedMkForThisDevice'];
  const identity = store.getters['courses-keys/deviceIdentity'];
  const status = statusOf(store);

  if (!wrapping) return failed(ADOPT_FAILURE.NO_WRAPPING);
  if (!identity?.devicePrivateKey) return failed(ADOPT_FAILURE.NO_DEVICE_KEY);
  if (!status?.keyId) return failed(ADOPT_FAILURE.NO_KEY_ID);

  let mk;
  try {
    mk = await tools.unwrapMasterKeyFromDevice({
      wrapping,
      memberId,
      keyId: status.keyId,
      devicePrivateKey: identity.devicePrivateKey,
      devicePublicKey: identity.devicePublicKey,
      // ⚠️ WITHOUT THIS EVERY `v: 2` APPROVAL FAILS AS AUTH_FAILED. The secret
      // is half the wrapping key, and it is THIS device's own S — the one it
      // generated and showed on screen 2 — not anything the approver sent. A
      // `v: 1` wrapping ignores it, so passing it always is right; the unwrap
      // tries both carriers because this device is never told which the
      // approver used (`approval-secret.js`).
      secret: identity.approvalSecret,
    });
  } catch (error) {
    // The one failure that must not be swallowed: the device stays keyless, and
    // the member has to be told rather than shown an empty Hub that claims to
    // be readable.
    console.warn('[CoursesE2EE] The wrapped key did not open on this device:', error);
    return failed(ADOPT_FAILURE.UNWRAP, error);
  }

  try {
    await store.dispatch('courses-keys/persist', {
      memberId,
      keyId: status.keyId,
      mk,
      ...identity,
    });
  } catch (error) {
    return failed(ADOPT_FAILURE.PERSIST, error);
  }

  // Best effort, and never a reason to fail an approval that has happened: the
  // key is in the key store either way, and a member whose Keychain refused the
  // write relies on approval or a recovery code, which the nudge says.
  try {
    const adapter = tools.resolveAdapter();
    if (adapter.isEligible()) {
      await adapter.put(
        memberId,
        tools.serialisePlatformCopy({ memberId, keyId: status.keyId, mk }),
      );
    }
  } catch (error) {
    console.warn('[CoursesE2EE] The platform copy of the key was not written:', error);
  }

  store.commit('courses/clearUnreadableAnnotations');
  store.commit('courses-keys/setEnrolment', {
    status: tools.ENROLMENT_STATUS.ENROLLED,
    hasRecoveryCode: store.getters['courses-devices/hasRecoveryCode'] === true,
  });
  await deps.refreshAnnotations(store);
  return outcome(APPROVAL_OUTCOME.APPROVED);
}

/**
 * What a device that cannot read the member's notes does on arrival. Safe to
 * call on every entry to the approval screen: a device that already holds the
 * key costs one status read and nothing else.
 *
 * Never rejects — the caller is a screen opening — so every answer, including
 * every failure, comes back as an outcome.
 *
 * @param {{ store: object, deps?: object }} args
 * @returns {Promise<{ outcome: string, reason?: string, error?: object, deviceId?: string }>}
 */
export async function resolveKeylessDevice({ store, deps: overrides } = {}) {
  if (!store) throw new TypeError('resolveKeylessDevice needs the root Vuex store');
  const deps = withDeps(overrides);

  // A deep link into the screen can beat the hydration App.vue starts, and a
  // member whose keys have not been read yet is not a member without keys.
  if (store.getters['courses-keys/isHydrated'] !== true) {
    try {
      await store.dispatch('courses-keys/ready', { timeoutMs: HYDRATION_TIMEOUT_MS });
    } catch (error) {
      return failed('not-hydrated', error);
    }
  }

  const memberId = store.getters['courses-keys/memberId'];
  if (!Number.isInteger(memberId) || memberId <= 0) return failed('no-member');

  // The API's own answer, never this device's guess.
  try {
    await store.dispatch('courses-devices/refreshStatus');
  } catch (error) {
    return failed('status-failed', error);
  }
  if (store.getters['courses-devices/enrolled'] !== true) {
    return outcome(APPROVAL_OUTCOME.NOT_ENROLLED);
  }

  const hasKey = store.getters['courses-keys/hasMasterKey'] === true;
  const needsRegistration = store.state['courses-keys']?.needsRegistration === true;
  if (hasKey && needsRegistration) return selfApprove({ store, deps });
  if (hasKey) return outcome(APPROVAL_OUTCOME.HAS_KEY);

  const tools = await deps.loadKeyTools();
  const status = store.getters['courses-devices/thisDeviceStatus'];

  if (status === DEVICE_STATUSES.REVOKED) return outcome(APPROVAL_OUTCOME.DENIED);
  if (status === DEVICE_STATUSES.APPROVED) {
    return store.getters['courses-devices/wrappedMkForThisDevice']
      ? adoptWrappedKey({ store, deps })
      : outcome(APPROVAL_OUTCOME.APPROVED_NO_WRAPPING);
  }
  if (status === DEVICE_STATUSES.PENDING) return outcome(APPROVAL_OUTCOME.PENDING);

  // UNREGISTERED. Register unsigned — there is no key to sign with — and the
  // API creates the row PENDING for an approved device to answer.
  if (status !== UNREGISTERED) return failed(`unknown-status:${status}`);
  try {
    await ensureLocalIdentity({ store, deps, tools });
    await store.dispatch('courses-devices/registerThisDevice');
  } catch (error) {
    if (
      isEncryptionApiError(error, ENCRYPTION_ERROR_CODES.CONFLICT) &&
      error.apiCode === API_ERROR_CODES.TOO_MANY_PENDING_DEVICES
    ) {
      return outcome(APPROVAL_OUTCOME.TOO_MANY_PENDING);
    }
    return failed('register-failed', error);
  }
  return outcome(APPROVAL_OUTCOME.PENDING);
}

/**
 * Resolve once `predicate(store)` holds, watching the store rather than
 * polling it.
 *
 * @param {object} store
 * @param {(store: object) => boolean} predicate
 */
function waitForStore(store, predicate) {
  if (predicate(store)) return Promise.resolve();
  return new Promise(resolve => {
    const unwatch = store.watch(
      () => predicate(store),
      held => {
        if (!held) return;
        unwatch();
        resolve();
      },
    );
  });
}

/** One wait per store at a time; every caller behind the one poll shares its answer. */
const waits = new WeakMap();

/**
 * Wait for an approved device to answer, and adopt the key the moment it does.
 * This is what makes the acceptance criterion true: the waiting device picks up
 * its wrapped key with no reload.
 *
 * The poll belongs to `courses-devices`, which ends it itself when its `until`
 * holds — so this waits for the poll to STOP rather than racing it. The caller
 * leaving the screen stops it too, which is the same signal; that case comes
 * back as PENDING, so nothing navigates on behalf of a member who has left.
 *
 * WHAT IT IS ACTUALLY WAITING FOR is the WRAPPING, not a change of status. A
 * device can go APPROVED with `wrappedMk: null` — a registration that proves
 * itself is approved whether or not it sent a `selfWrapped` (contract section
 * 4) — and that device still cannot read one word the member wrote. Ending the
 * wait there would leave it on a screen with no spinner, waiting for nothing.
 * So only a wrapping, or a refusal, is an answer.
 *
 * @param {{ store: object, intervalMs?: number, deps?: object }} args
 * @returns {Promise<{ outcome: string }>} ONE promise per store while a wait is
 *   pending: a second caller gets the same one, and the answer once
 */
export function awaitApproval({ store, intervalMs = POLL_INTERVAL_MS, deps: overrides } = {}) {
  // Single-flight per store, as `refreshStatus` and `enrolIfEligible` are: the
  // wait ends when the module's ONE poll stops, so two waiters — the page's
  // entry wait and a Try again (#1652) — would both wake on the key's arrival
  // and both adopt it. A second caller joins the first.
  const running = waits.get(store);
  if (running) return running;
  const run = awaitAnswer({ store, intervalMs, deps: withDeps(overrides) }).finally(() => {
    if (waits.get(store) === run) waits.delete(store);
  });
  waits.set(store, run);
  return run;
}

async function awaitAnswer({ store, intervalMs, deps }) {
  // ⚠️ LOCAL getter names, NOT namespaced ones. `startPolling` calls `until`
  // with its own `context.getters`, which in a namespaced module is the
  // module's local map — so `getters['courses-devices/wrappedMkForThisDevice']`
  // is `undefined` and a `!==` comparison against it is TRUE. A namespaced key
  // here does not throw: it ends the wait on the FIRST tick, and the waiting
  // device then sits for ever on a screen that has stopped asking for the key
  // it is waiting for. That is the acceptance criterion, broken silently, which
  // is why both shapes below are pinned by tests.
  const answered = getters =>
    !!getters.wrappedMkForThisDevice || getters.thisDeviceStatus === DEVICE_STATUSES.REVOKED;

  await store.dispatch('courses-devices/startPolling', { intervalMs, until: answered });
  await waitForStore(store, s => s.state['courses-devices'].polling !== true);

  if (store.getters['courses-devices/wrappedMkForThisDevice']) {
    return adoptWrappedKey({ store, deps });
  }
  const status = store.getters['courses-devices/thisDeviceStatus'];
  if (status === DEVICE_STATUSES.REVOKED) return outcome(APPROVAL_OUTCOME.DENIED);
  if (status === DEVICE_STATUSES.APPROVED) return outcome(APPROVAL_OUTCOME.APPROVED_NO_WRAPPING);
  return outcome(APPROVAL_OUTCOME.PENDING);
}

/**
 * "Request again", after a refusal.
 *
 * A REVOKED row is never upgraded (device.service.ts:49-53), so re-sending this
 * identity would answer 201 with the same refusal. Both halves have to be new:
 * a new id AND a new key pair, stored, and registered unsigned.
 *
 * @param {{ store: object, deps?: object }} args
 */
export async function requestAgain({ store, deps: overrides } = {}) {
  const deps = withDeps(overrides);
  const tools = await deps.loadKeyTools();
  try {
    await ensureLocalIdentity({ store, deps, tools, fresh: true });
    await store.dispatch('courses-devices/registerThisDevice');
  } catch (error) {
    if (
      isEncryptionApiError(error, ENCRYPTION_ERROR_CODES.CONFLICT) &&
      error.apiCode === API_ERROR_CODES.TOO_MANY_PENDING_DEVICES
    ) {
      return outcome(APPROVAL_OUTCOME.TOO_MANY_PENDING);
    }
    return failed('register-failed', error);
  }
  return outcome(APPROVAL_OUTCOME.PENDING);
}

/**
 * Approve a device asking to join, from the device that holds MK. This is the
 * moment the member IS the authority: the server cannot grant approval, only
 * check a signature made with a key it never sees (decision 23).
 *
 * @param {object} args
 * @param {object} args.store the root Vuex store
 * @param {{ id: string, publicKey: string }} args.device the PENDING row
 * @param {Uint8Array} [args.secret] the out-of-band S the member read off the
 *   waiting phone — sixteen bytes from a scan, ten from a typed code. WITH it
 *   the wrapping is `v: 2` and decision 15 is closed; without it `v: 1`, which
 *   is what the screen's explicit older-version fallback chooses. The screens
 *   are what make it non-optional in practice: Approve is disabled until one
 *   of the three succeeds.
 * @param {string} [args.verifiedPublicKey] the base64 SPKI the member's own
 *   camera read, when a scan matched. The wrap goes to THIS rather than to the
 *   row's copy of it.
 * @param {object} [args.deps]
 */
export async function approvePendingDevice({
  store,
  device,
  secret,
  verifiedPublicKey,
  deps: overrides,
} = {}) {
  const deps = withDeps(overrides);
  if (!device?.id || !device?.publicKey) return failed('no-device');

  // ⚠️ THE RACE DECISION 15 LEAVES OPEN, closed here. The scan compared the
  // code against the row as it was READ; the status poll refreshes every four
  // seconds, so a substitution landing between the scan and the tap would hand
  // MK to whatever key arrived last — the same attack, executed one second
  // later. If the member verified a key, the row must still be carrying it.
  //
  // Nothing is dispatched: the wrapping is never made, so there is nothing on
  // the wire to withdraw and no nonce spent. The caller turns this reason into
  // the same refusal a mismatched scan produces, so the member is told rather
  // than left tapping a button that does nothing.
  //
  // A STRING compare here, where the scan's is byte-wise, and the difference is
  // real rather than an oversight. `approval-scan.js` compares BYTES because it
  // holds two renderings from two sources — the device's own export and the
  // API's stored copy — which may legitimately differ as text. These two are
  // the same field of the same row, read from the same server minutes apart: a
  // difference in the string IS a difference in the row.
  if (verifiedPublicKey && device.publicKey !== verifiedPublicKey) {
    console.warn('[CoursesE2EE] The pending device public key changed after it was verified');
    return failed('public-key-changed');
  }

  const tools = await deps.loadKeyTools();
  const memberId = store.getters['courses-keys/memberId'];
  const keys = store.getters['courses-keys/keyMaterial'];
  if (!keys) return failed('no-master-key');

  // The bundle's `keyId` and `signingKeyWrapped` only exist on a status
  // snapshot, so one is read here when there is none. In practice the host
  // screen has already read it — the pending device it renders came from that
  // read — but depending on the caller's ordering for the difference between a
  // working Approve button and a dead one is not worth the one GET.
  if (!statusOf(store)) {
    try {
      await store.dispatch('courses-devices/refreshStatus');
    } catch (error) {
      return failed('status-failed', error);
    }
  }
  const status = statusOf(store);
  if (!status?.keyId) return failed('no-key-id');

  let wrappedMk;
  let signer;
  try {
    // To the PENDING device's OWN public key: only its private half opens
    // this, which is what makes the server's copy useless to anybody else.
    //
    // THE VERIFIED KEY WINS over the row's. They are equal by the guard above,
    // so this changes no byte today — it changes what the code PROMISES: what
    // MK is wrapped to is the key the member's camera read, not a value the
    // API handed us and we checked once. `secret` is what makes it `v: 2`.
    wrappedMk = await tools.wrapMasterKeyToDevice({
      mk: keys.mk,
      keyId: status.keyId,
      memberId,
      devicePublicKey: verifiedPublicKey ?? device.publicKey,
      secret,
    });
    signer = await signerFromStore({ store, deps });
  } catch (error) {
    return failed('wrap-failed', error);
  }

  try {
    await store.dispatch('courses-devices/approveDevice', { id: device.id, wrappedMk, signer });
  } catch (error) {
    // Recorded in `courses-devices` state.error, which is what the screen
    // renders — including whether the member can usefully tap Try again.
    return failed('approve-failed', error);
  }
  return outcome(APPROVAL_OUTCOME.APPROVED, { deviceId: device.id });
}

/**
 * Refuse a device asking to join.
 *
 * Unsigned: the deployed controller puts no `SignedOperationGuard` on
 * `PUT /devices/:id/deny` (encryption.controller.ts:96-99). Denying only ever
 * revokes a device that has no key yet, so it destroys nothing — and asking for
 * a challenge would spend one for nothing.
 *
 * @param {{ store: object, device: { id: string } }} args
 */
export async function denyPendingDevice({ store, device } = {}) {
  if (!device?.id) return failed('no-device');
  try {
    await store.dispatch('courses-devices/denyDevice', { id: device.id });
  } catch (error) {
    return failed('deny-failed', error);
  }
  return outcome(APPROVAL_OUTCOME.DENIED, { deviceId: device.id });
}

/** Specs reset the memoised crypto between cases; the app never calls this. */
export function resetDeviceApprovalStateForTests() {
  keyToolsPromise = null;
}
